import React from 'react';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import {
    GroupHeader,
    GroupTitle,
    GroupCount,
    StagesGrid,
    DropZone,
    HelpText
} from '../styles';
import { DefaultStageMappingType } from '../../../api/integrations';
import StageCard from './StageCard';
import { GroupedStage } from '../types/stageTypes';

interface StageGroupProps {
    stageType: DefaultStageMappingType | 'UNMAPPED' | 'HIDDEN';
    label: string;
    stages: GroupedStage[];
    isExpanded: boolean;
    selectedStages: Set<string>;
    onToggleExpand: () => void;
    onToggleSelection: (id: string) => void;
    onToggleAlwaysUnmap?: (id: string, unmapped: boolean) => void;
}

const StageGroup: React.FC<StageGroupProps> = ({
    stageType,
    label,
    stages,
    isExpanded,
    selectedStages,
    onToggleExpand,
    onToggleSelection,
    onToggleAlwaysUnmap
}) => {
    const { t } = useTranslation();
    const isInterview = stageType === 'INTERVIEW';
    // Use full-width layout when it's an interview card or there's only one card in the group
    const useFullWidthLayout = isInterview || stages.length === 1;

    return (
        <div>
            {/* Group header */}
            <GroupHeader onClick={onToggleExpand}>
                <GroupTitle>
                    {isExpanded
                        ? <UpOutlined style={{ fontSize: '12px' }} />
                        : <DownOutlined style={{ fontSize: '12px' }} />
                    }
                    <span style={{ marginLeft: '6px' }}>{label}</span>
                    <GroupCount>({stages.length})</GroupCount>
                </GroupTitle>
            </GroupHeader>

            {/* Group content */}
            {isExpanded && (
                <>
                    <Droppable droppableId={stageType}>
                        {(provided) => (
                            <StagesGrid
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                $isListLayout={useFullWidthLayout}
                            >
                                {/* Stage cards */}
                                {stages.map((stage, index) => (
                                    <Draggable
                                        key={stage.id}
                                        draggableId={stage.id}
                                        index={index}
                                    >
                                        {(dragProvided) => (
                                            <StageCard
                                                id={stage.id}
                                                name={stage.name}
                                                stageType={stage.mapping.stage_type}
                                                order={stage.mapping.order}
                                                isUnmapped={stage.mapping.is_unmapped}
                                                isSelected={selectedStages.has(stage.id)}
                                                isChanged={stage.isChanged}
                                                isInterview={isInterview}
                                                innerRef={dragProvided.innerRef}
                                                draggableProps={dragProvided.draggableProps}
                                                dragHandleProps={dragProvided.dragHandleProps}
                                                onToggleSelection={onToggleSelection}
                                                onToggleAlwaysUnmap={onToggleAlwaysUnmap}
                                            />
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}

                                {/* Empty state */}
                                {stages.length === 0 && (
                                    <DropZone>
                                        {stageType === 'UNMAPPED'
                                            ? t('ats.ALL_STAGES_MAPPED')
                                            : <span>{t('ats.DRAG_TO_MAP_AS')} <b>{label}</b></span>
                                        }
                                    </DropZone>
                                )}
                            </StagesGrid>
                        )}
                    </Droppable>

                    {/* Help text for interview reordering */}
                    {isInterview && stages.length > 1 && (
                        <HelpText>
                            Drag to reorder interview stages
                        </HelpText>
                    )}
                </>
            )}
        </div>
    );
};

export default StageGroup; 